import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const navGroups = [
  {
    title: 'VPS Trader',
    links: [
      { label: 'Trader Brasil',  path: '/vps-trader-br' },
      { label: 'Trader Londres', path: '/vps-trader-uk' },
      { label: 'Trader EUA',     path: '/vps-trader-us' },
    ],
  },
  {
    title: 'VPS Pessoal',
    links: [
      { label: 'VPS Pessoal',   path: '/vps-pessoal' },
    ],
  },
  {
    title: 'VPS Devs',
    links: [
      { label: 'VPS para Devs', path: '/vps-dev' },
    ],
  },
  {
    title: 'VPS Empresa',
    links: [
      { label: 'VPS Empresa',   path: '/vps-empresa' },
    ],
  },
  {
    title: 'Empresa',
    links: [
      { label: 'Quem Somos',   path: '/sobre' },
      { label: 'Data Centers', path: '/data-center' },
      { label: 'Contato',      path: '/contato' },
      { label: 'SLA',          path: '/sla' },
      { label: 'Mapa do Site', path: '/mapa-do-site' },
    ],
  },
  {
    title: 'Suporte',
    links: [
      { label: 'Área do Cliente',  path: 'https://musthost.com.br/sac',    external: true },
      { label: 'Abrir Chamado',    path: '/contato' },
      { label: 'Tutoriais',        path: '/tutoriais' },
      { label: 'Status',           path: 'https://status.musthost.com.br', external: true },
    ],
  },
];

const socials = [
  { icon: 'ri-youtube-line',   href: 'https://www.youtube.com/@musthost',         label: 'YouTube' },
  { icon: 'ri-instagram-line', href: 'https://www.instagram.com/musthost',        label: 'Instagram' },
  { icon: 'ri-linkedin-line',  href: 'https://www.linkedin.com/company/musthost', label: 'LinkedIn' },
];

const payments = [
  {
    id: 'visa',
    node: (
      <div className="flex items-center justify-center px-2.5 py-1 rounded bg-[#1A1F71] select-none h-6">
        <span className="text-white text-xs font-black tracking-widest">VISA</span>
      </div>
    ),
  },
  {
    id: 'mc',
    node: (
      <div className="flex items-center justify-center gap-0 px-2 py-1 rounded bg-[#252525] border border-white/8 select-none h-6">
        <div className="w-3 h-3 rounded-full bg-[#EB001B] shrink-0" />
        <div className="w-3 h-3 rounded-full bg-[#F79E1B] -ml-1.5 shrink-0" />
        <span className="text-white text-xs font-medium ml-1 whitespace-nowrap">MC</span>
      </div>
    ),
  },
  {
    id: 'elo',
    node: (
      <div className="flex items-center justify-center px-2.5 py-1 rounded bg-[#FFD100] select-none h-6">
        <span className="text-black text-xs font-black lowercase tracking-tight">elo</span>
      </div>
    ),
  },
  {
    id: 'pix',
    node: (
      <div className="flex items-center gap-1 px-2.5 py-1 rounded bg-[#32BCAD] select-none h-6">
        <i className="ri-qr-code-line text-white text-xs" />
        <span className="text-white text-xs font-black tracking-wider">PIX</span>
      </div>
    ),
  },
  {
    id: 'boleto',
    node: (
      <div className="flex items-center gap-1 px-2.5 py-1 rounded bg-[#252525] border border-white/8 select-none h-6">
        <i className="ri-file-list-3-line text-orange-400 text-xs" />
        <span className="text-gray-300 text-xs font-medium">Boleto</span>
      </div>
    ),
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <footer className="bg-[#080809]">

      {/* ── MAIN ──────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-8 pt-16 pb-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* Brand */}
          <div className="lg:w-64 shrink-0 flex flex-col gap-6">
            <button onClick={() => navigate('/')} className="cursor-pointer self-start">
              <img
                src="https://static.readdy.ai/image/42308ee72db4ea3b7b8c06c6003bbb62/924ba9f4334fddff300aee3ddd13592c.png"
                alt="MUSTHOST"
                width={120}
                height={40}
                loading="lazy"
                decoding="async"
                className="h-10 w-auto object-contain brightness-0 invert opacity-80"
              />
            </button>
            <p className="text-[#4a4a55] text-sm leading-relaxed">
              {t('footer_about')}
            </p>
            <div className="flex items-center gap-1.5">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-[#3a3a45] hover:text-[#888] transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Nav */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6">
            {navGroups.map((col) => (
              <div key={col.title}>
                <p className="text-[#333340] text-xs font-semibold uppercase tracking-widest mb-5">
                  {col.title}
                </p>
                <ul className="space-y-3">
                  {col.links.map((link) => {
                    const external = (link as { label: string; path: string; external?: boolean }).external;
                    if (external) {
                      return (
                        <li key={link.label}>
                          <a
                            href={link.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#4a4a58] hover:text-[#aaaabc] text-sm transition-colors cursor-pointer block whitespace-nowrap"
                          >
                            {link.label}
                          </a>
                        </li>
                      );
                    }
                    return (
                      <li key={link.label}>
                        <a
                          href={link.path}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(link.path);
                          }}
                          className="text-[#4a4a58] hover:text-[#aaaabc] text-sm transition-colors cursor-pointer block whitespace-nowrap"
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── DIVIDER ───────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-8">
        <div className="border-t border-[#16161e]" />
      </div>

      {/* ── BOTTOM BAR ────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-8 py-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

          {/* Left: legal */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <p className="text-[#333340] text-xs whitespace-nowrap">
              © {new Date().getFullYear()} MUSTHOST LTDA · CNPJ 12.884.720/0001-46
            </p>
            <span className="hidden md:block text-[#222228] text-xs">|</span>
            {[
              { label: 'Termos',      path: '/termos-de-uso' },
              { label: 'Privacidade', path: '/politica-de-privacidade' },
              { label: 'Cookies',     path: '/politica-de-cookies' },
              { label: 'SLA',         path: '/sla' },
            ].map((link) => (
              <a
                key={link.label}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                }}
                className="text-[#333340] hover:text-[#666] text-xs transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right: status + payments */}
          <div className="flex items-center gap-4 flex-wrap">
            <a
              href="https://status.musthost.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 cursor-pointer group"
            >
              <span className="relative flex w-1.5 h-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50" />
                <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-green-500" />
              </span>
              <span className="text-[#3a4a3a] group-hover:text-green-600 text-xs transition-colors whitespace-nowrap">Operacional</span>
            </a>
            <span className="text-[#1e1e25] text-xs">|</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {payments.map((p) => <div key={p.id}>{p.node}</div>)}
            </div>
          </div>

        </div>
      </div>

    </footer>
  );
}
